// src/GoogleAuthProvider.tsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { initGoogleAuth, onAuthChange, getAccessToken, requestInteractive } from './auth/google';

type Ctx = { 
  isAuthed: boolean; 
  connect: () => void; 
  getAuthHeader: () => HeadersInit | null; 
};

const AuthContext = createContext<Ctx | null>(null);

export const GoogleAuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [isAuthed, setIsAuthed] = useState<boolean>(!!getAccessToken());
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;          // StrictMode guard
    started.current = true;

    initGoogleAuth();
    const off = onAuthChange(() => setIsAuthed(!!getAccessToken()));
    return () => off();
  }, []);

  const value = useMemo<Ctx>(() => ({
    isAuthed,
    connect: () => requestInteractive(),
    getAuthHeader: () => {
      const t = getAccessToken();
      return t ? { Authorization: `Bearer ${t}` } : null;
    },
  }), [isAuthed]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useGoogleAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useGoogleAuth must be used within GoogleAuthProvider');
  return ctx;
}